import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { useBreakpointValue } from '@notino/react-styleguide';
import {
  GetCatalogProductViewQuery,
  ProductIdType,
} from '@notino/shared/definitions/types';

import { ProductSlider } from '@components/ProductSlider';
import { useGetProductItemProps } from '@components/Universals/ProductItem/useGetProductItemProps';
import { useWishlistLists } from '@components/Universals/WishlistModifier/useWishlistLists';
import {
  useTrackToCartAsync,
  useTrackToCartFailedAsync,
} from '@helpers/googleTagManager/vpProductCartTracking';

import { useProductsByIdsPaginated } from '../../ProductsSection/useProductsByIdsBatched';
import { useTrackExtras } from '../hooks/useTrackExtras';

import { messages } from './messages';
import { SliderWrapper, StyledLoadingPlaceholder, Title } from './styled';

const GTM_LIST_LABEL = 'gift_sets';
const ITEMS_PER_SLIDE = 3;

type Props = {
  sets: GetCatalogProductViewQuery['productDetailByCatalogMasterId']['features']['availableSets']['sets'];
};

export const AvailableSetsList = ({ sets }: Props) => {
  const isDesktop = useBreakpointValue({ md: true });

  const mobileSliderRef = React.useRef<HTMLDivElement>();

  const catalogIds = sets.map((set) => `${set.catalogId}`);

  const { data, loading, loadMore } = useProductsByIdsPaginated(catalogIds, {
    idType: ProductIdType.Catalog,
    pageSize: ITEMS_PER_SLIDE * 2,
  });

  const { onNewItemShow, onSlideChange } = useTrackExtras({
    catalogIds,
    data,
    listName: GTM_LIST_LABEL,
    trackingEventAction: 'gift_sets_slide',
    trackingEventName: 'gift_sets_slide',
    trackingEventType: 'gift_sets',
    wasScrollLeft: mobileSliderRef.current?.scrollLeft === 0,
  });

  const trackAddToCartFailedAsync = useTrackToCartFailedAsync(
    catalogIds,
    GTM_LIST_LABEL,
    true
  );
  const trackAddToCardAsync = useTrackToCartAsync(
    catalogIds,
    GTM_LIST_LABEL,
    true
  );

  const getProductItemProps = useGetProductItemProps({
    noPriceIfUnavailable: true,
    useMasterUrl: true,
    inSectionOfType: 'slider',
    wishlistActionLocation: 'list',
    onProductAddFailed: trackAddToCartFailedAsync,
    showBuyButton: true,
    showAddToCartModal: false,
    GTMEventLabel: GTM_LIST_LABEL,
    sendGTM: true,
    onProductAdded: trackAddToCardAsync,
    listIdForWishlistOperations: useWishlistLists.DEFAULT_LIST_ID,
  });

  const placeholderCount = Math.min(sets.length, ITEMS_PER_SLIDE);
  const centerPlaceholder = placeholderCount < ITEMS_PER_SLIDE;

  const products =
    data
      .map((product, index) =>
        getProductItemProps({
          product,
          position: index + 1,
        })
      )
      .filter(Boolean) ?? [];

  return (
    <div>
      {isDesktop && (
        <Title>
          <FormattedMessage {...messages.setsWithThisProduct} />
        </Title>
      )}

      {!data || loading ? (
        <StyledLoadingPlaceholder
          placeholderCount={placeholderCount}
          rows={5}
          center={centerPlaceholder}
        />
      ) : (
        <SliderWrapper centerSlider={products.length < ITEMS_PER_SLIDE}>
          <ProductSlider
            mobileScrollContainerRef={mobileSliderRef}
            itemsPerSlide={ITEMS_PER_SLIDE}
            products={products ?? []}
            onItemShow={({ product }) => {
              onNewItemShow(product.id);
              // we fetch 2 slides at once, so when last item of first slide is shown,
              // we fetch next page to prevent carousel glitch
              if (
                product.id ===
                data[Math.max(data.length - 1 - ITEMS_PER_SLIDE, 0)]?.id
              ) {
                loadMore();
              }
            }}
            inViewThreshold={0.55}
            onSlideChange={onSlideChange}
            fallback={
              <StyledLoadingPlaceholder
                placeholderCount={placeholderCount}
                rows={4}
                center={centerPlaceholder}
              />
            }
          />
        </SliderWrapper>
      )}
    </div>
  );
};
